import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SearchCountryField, CountryISO, PhoneNumberFormat } from 'ngx-intl-tel-input-gg';
import { AuthService } from '../../services/auth.service';
import { Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, finalize, firstValueFrom, pipe, Subject } from 'rxjs';
import { AddressHereService } from '../../services/address-here.service';
import { OrganizationService } from '../../services/organization.service';
import { StripeService } from '../../services/Stripe/stripe.service';
import { StripeThemeService } from '../../services/stripe-theme.service';
import { loadStripe, Stripe, StripeElements, StripePaymentElement } from '@stripe/stripe-js';
import { jobTitles } from './jobtitles';
import { ActivatedRoute } from '@angular/router';
import { ToastService } from '../../services/toast.service';
import { OtpAction, Utilities } from '../../shared/utils';
import { ValidatorUtil } from '../../shared/utilities/validator.utility';
import { LoaderService } from '../../services/loader.service';
import { PlatformUtilities } from '../../shared/PlatformUtilities';

interface RegistrationData {
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  orgCategory?: string;
  company?: string;
  jobTitle?: string;
}

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrl: './register.component.css',
})
export class RegisterComponent implements OnInit {
  private readonly key = '1234567890123456';
  private readonly iv = '6543210987654321';

  numberPlaceholder = '07700 168247';
  loginViaEmail: boolean = true;
  emailOrMobile: string = '';
  emailError: any = '';
  submitted = false;
  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
  phoneForm = new FormGroup({
    phone: new FormControl('', [Validators.required]),
  });
  preferredCountries: CountryISO[] = [CountryISO.UnitedStates, CountryISO.UnitedKingdom];
  separateDialCode = false;
  minutes: number = 2; // Start at 0 minutes
  seconds: number = 0; // Start at 59 seconds
  timerInterval: any;
  showAddressManually = false;
  searchSubject = new Subject<any>();
  selectedAddress: string | null = null;
  addresses = [];
  wholeAddresses: any = [];
  addressPlaceholder = 'Start typing to search for an address...';
  addressPosition: any;
  invalidOTP: boolean = false;
  rememberMe: boolean = false;
  phoneVerified: boolean = false;
  orgDetails: any = [];
  successResponse: any;
  OTP: string | null = null;
  emailNumber: boolean = false;
  isZapiet: boolean = false;
  isShopify: boolean = false;
  stripe: Stripe | null = null;
  elements: StripeElements | null = null;
  paymentElement: StripePaymentElement | null = null;
  setupIntentId: string | null = null;
  clientSecret: string | null = null;
  isInitializingCardSetup: boolean = false;
  isSavingCard: boolean = false;
  cardError: string | null = null;
  generatedApiKey: string = '';
  isCopied: boolean = false;
  savePaymentModalInstance: any = null;
  apiKeyModalInstance: any = null;
  zapietUserData: { email: string; phone: string; orgCode: string; isZapiet?: boolean } = {
    email: '',
    phone: '',
    orgCode: '',
    isZapiet: false,
  };
  orgCategories = [
    { label: 'Business', value: 'BUS' },
    { label: 'Individual', value: 'NAT' },
    { label: 'Government', value: 'GOV' },
    { label: 'NGO', value: 'NGO' },
  ];
  orgCategoryError = false;
  jobTitleErr = false;
  orgCategoryPlaceholder = 'Select user type';
  personalDetails: {
    firstName: string;
    lastName: string;
    company: string | null;
    jobtitle: string | null;
    orgCategory: string | null;
  } = {
      firstName: '',
      lastName: '',
      company: null,
      jobtitle: null,
      orgCategory: null,
    };
  JobTitlePlaceholder = 'Select your job title';
  jobTitleList = jobTitles.map((title) => ({
    value: title,
    label: title,
  }));
  dropdownAddress = {
    address: '',
    postCode: '',
    city: '',
    companyName: '',
    countryCode: 'GB',
    establishment: '',
    address1: '',
    address2: '',
    coordinates: '',
    additionalDetails: '',
  };
  manualAddress = {
    address1: '',
    address2: '',
    city: '',
    postCode: '',
    establishment: '',
    countryCode: '',
    additionalDetails: '',
  };
  personalDetailsError = {
    firstName: '',
    lastName: '',
    company: '',
    address: '',
  };
  manualAddressError = {
    address1: '',
    address2: '',
    city: '',
    postCode: '',
  };
  errors: { [key: string]: boolean } = {};
  oldEmail: string = '';
  doorNumberPlaceholder: string = Utilities.DOOR_NUMBER_PLACEHOLDER;

  private decryptedData: RegistrationData = {
    email: '',
    phone: '',
    firstName: '',
    lastName: '',
    orgCategory: '',
    company: '',
    jobTitle: '',
  };

  constructor(
    private authService: AuthService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
    private addressService: AddressHereService,
    private organizationService: OrganizationService,
    private toast: ToastService,
    private route: ActivatedRoute,
    private loaderService: LoaderService,
    private platformUtilities: PlatformUtilities,
    private stripeService: StripeService,
    private stripeTheme: StripeThemeService
  ) { }

  ngOnInit() {
    this.route.queryParams.subscribe(async (params) => {
      this.detectZapietParam(params);
      const encrypted = params['data'];
      if (encrypted) {
        const decrypted = await this.decryptAES(encrypted);
        if (decrypted) {
          const queryParams = new URLSearchParams(decrypted);

          this.decryptedData = {
            email: queryParams.get('email') || '',
            phone: queryParams.get('phone') || '',
            firstName: queryParams.get('firstName') || '',
            lastName: queryParams.get('lastName') || '',
            orgCategory: queryParams.get('userType') || '',
            company: queryParams.get('orgName') || '',
            jobTitle: queryParams.get('jobTitle') || '',
          };

          this.emailOrMobile = this.decryptedData.email;

          if (!this.isZapiet && !this.isShopify) {
            for (const [key, value] of queryParams.entries()) {
              const k = key.toLowerCase();
              const v = value.toLowerCase();
              if (k === 'source' && v === 'zapiet') {
                this.isZapiet = true;
                this.isShopify = false;
                break;
              } else if (k === 'source' && v === 'shopify') {
                this.isShopify = true;
                this.isZapiet = false;
                break;
              } else if (k.includes('zapiet') || v.includes('zapiet')) {
                this.isZapiet = true;
                this.isShopify = false;
                break;
              } else if (k.includes('shopify') || v.includes('shopify')) {
                this.isShopify = true;
                this.isZapiet = false;
                break;
              }
            }
          }
        }
      }

      if (this.isZapiet || this.isShopify) {
        if (!this.personalDetails.orgCategory) {
          this.personalDetails.orgCategory = this.decryptedData.orgCategory || 'BUS';
        }
      }

      if (params['setup_intent_client_secret'] || params['setup_intent']) {
        this.handleSetupResult(params);
      }
    });

    this.addressSearchDebouncer();
  }

  private detectZapietParam(params: any): void {
    if (params) {
      const source = params['source']?.toLowerCase();
      if (source === 'zapiet') {
        this.isZapiet = true;
        this.isShopify = false;
        return;
      }
      if (source === 'shopify') {
        this.isShopify = true;
        this.isZapiet = false;
        return;
      }

      if (
        params['zapiet'] !== undefined ||
        Object.keys(params).some((key) => key.toLowerCase().includes('zapiet')) ||
        Object.values(params).some(
          (val) => typeof val === 'string' && val.toLowerCase().includes('zapiet')
        )
      ) {
        this.isZapiet = true;
        this.isShopify = false;
        return;
      }

      if (
        params['shopify'] !== undefined ||
        Object.keys(params).some((key) => key.toLowerCase().includes('shopify')) ||
        Object.values(params).some(
          (val) => typeof val === 'string' && val.toLowerCase().includes('shopify')
        )
      ) {
        this.isShopify = true;
        this.isZapiet = false;
        return;
      }
    }

    const currentUrl = this.router.url || '';
    if (currentUrl.toLowerCase().includes('zapiet')) {
      this.isZapiet = true;
      this.isShopify = false;
      return;
    }
    if (currentUrl.toLowerCase().includes('shopify')) {
      this.isShopify = true;
      this.isZapiet = false;
      return;
    }

    if (isPlatformBrowser(this.platformId) && typeof window !== 'undefined' && window?.location?.search) {
      const search = window.location.search.toLowerCase();
      if (search.includes('source=zapiet') || search.includes('zapiet')) {
        this.isZapiet = true;
        this.isShopify = false;
        return;
      }
      if (search.includes('source=shopify') || search.includes('shopify')) {
        this.isShopify = true;
        this.isZapiet = false;
        return;
      }
    }
  }

  addressSearchDebouncer() {
    this.searchSubject.pipe(debounceTime(Utilities.ADDRESS_SEARCH_DEBOUNCER_TIME)).subscribe((searchTerm: any) => {
      this.handleAddressSearch(searchTerm);
    });
  }

  handleAddressSearch(searchTerm: any) {
    this.addressPlaceholder = '';

    if (searchTerm?.term?.length > 2) {
      this.fetchAddresses(searchTerm);
    } else {
      this.addressPlaceholder = 'Start typing to search for an address...';
      this.addresses = [];
    }
  }

  onAddressChange(selectedAddress: any): void {
    if (selectedAddress.length > 0) {
      this.addressPlaceholder = '';
      this.errors['address'] = false;
      const selectedIndex = this.addresses.findIndex((address) => address === selectedAddress);
      if (selectedIndex !== -1) {
        const selectedWholeAddress = this.wholeAddresses[selectedIndex];

        this.dropdownAddress.address = selectedAddress;
        this.dropdownAddress.postCode = selectedWholeAddress?.postalCode;
        this.dropdownAddress.city = selectedWholeAddress?.postalTown;
        this.dropdownAddress.coordinates = selectedWholeAddress?.geocode?.results[0]?.geometry.location || {};
        this.dropdownAddress.countryCode = selectedWholeAddress?.countryCode;

        this.dropdownAddress.establishment = selectedWholeAddress?.establishment;
        this.dropdownAddress.address1 = selectedWholeAddress?.address1;
        this.dropdownAddress.address2 = selectedWholeAddress?.address2;
        this.dropdownAddress.additionalDetails = selectedWholeAddress?.streetNumber;
        if (!this.dropdownAddress.city || !this.dropdownAddress.postCode || !this.dropdownAddress.address1) {
          this.showAddressManually = true;
          this.onManualAddressToggle();
        }
      }
    } else {
      this.addressPlaceholder = 'Type to search for an address...';
    }
    // Handle the selected address
  }

  getPostalTownFromDescription(description: string): string {
    const match = this.wholeAddresses.find((addr: any) => addr.description === description);
    if (!match) return '';

    const components = match.geocode?.results?.[0]?.address_components || [];

    const postalTown = components.find((comp: any) => comp.types.includes('postal_town'));

    return postalTown?.long_name || null;
  }

  onAddressSearch(event: any): void {
    const searchTerm = event.term.toLowerCase();
    this.searchSubject.next({ term: searchTerm });
  }

  fetchAddresses(searchTerm: any): void {
    this.addressService.getAddressFromBackend(searchTerm.term).subscribe(
      (response) => {
        if (response.predictions && Array.isArray(response.predictions)) {
          this.addresses = response.predictions.map((item: any) => {
            const description = item?.description || '';
            return description;
          });
          this.wholeAddresses = response.predictions;
        } else {
          this.addresses = [];
          this.wholeAddresses = null;
        }
      },
      (error) => {
        console.error('Error fetching addresses:', error);
        this.addresses = [];
        this.wholeAddresses = null;
      }
    );
  }

  adjustTextarea(event: Event) {
    if (this.errors['address']) delete this.errors['address'];

    const textarea = event.target as HTMLTextAreaElement;
    textarea.style.height = 'auto'; // Reset height to recalculate
    textarea.style.height = Math.min(textarea.scrollHeight, 100) + 'px'; // Set new height, respecting max-height
  }

  startTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval); // Clear any existing interval
    }

    this.timerInterval = setInterval(() => {
      if (this.seconds > 0) {
        this.seconds--;
      } else if (this.minutes > 0) {
        this.minutes--;
        this.seconds = 59;
      } else {
        clearInterval(this.timerInterval); // Stop the timer at 0
      }
    }, 1000); // Decrement every second
  }

  resendCode() {
    // Logic for resending code
    alert('Resending code...');
    this.invalidOTP = false;
    this.resetTimer();
    this.submitEmailOrMobile(this.emailOrMobile);
  }

  resetTimer() {
    clearInterval(this.timerInterval); // Ensure no duplicate intervals
    this.minutes = 2;
    this.seconds = 0;
    this.startTimer();
  }

  toggleLoginMethod() {
    this.loginViaEmail = !this.loginViaEmail;
    this.emailError = null;
    this.submitted = false;
    this.emailOrMobile = '';
    this.phoneForm.reset();
  }

  onOtpChange(event: any) {
    if (event.length === 6) {
      this.OTP = event;
    }
  }

  emailChanged() {
    this.emailError = '';
  }

  submitEmailOrMobile(emailOrMobile: string) {
    this.loaderService.start();
    this.authService.authenticateOrVerify(emailOrMobile, OtpAction.VERIFY)
      .pipe(finalize(() => this.loaderService.stop()))
      .subscribe({
        next: (response) => {
          this.successResponse = response;
          this.submitted = true;
          this.OTP = null;
          this.invalidOTP = false;
          this.resetTimer();
          this.startTimer();
        },
        error: (error) => {
          const msg = Utilities.getErrorMessage(error);
          this.emailOrMobile = '';
          this.emailError = msg;
        },
      });

    setTimeout(() => {
      this.loaderService.stop();
      this.submitted = true;
    }, Utilities.EMPTY_LOADER_TIMER);
  }

  checkOTP() {
    if (!this.OTP) {
      this.invalidOTP = true;
      return;
    }

    let validateObjet;

    if (this.loginViaEmail) {
      validateObjet = {
        Email: this.emailOrMobile,
        OTP: this.OTP,
      };

      this.phoneForm;
    } else {
      validateObjet = {
        Email: this.oldEmail,
        Mobile: this.emailOrMobile,
        OTP: this.OTP,
      };
    }

    this.authService.validate(validateObjet).subscribe({
      next: (response) => {
        this.successResponse = response;
        this.invalidOTP = false;
        if (this.loginViaEmail) {
          setTimeout(() => {
            this.loaderService.stop();
            this.loginViaEmail = false;
            this.submitted = false;
            const phone = this.decryptedData.phone;
            if (phone) {
              this.phoneForm.get('phone')?.setValue(phone);
            }
          }, 2000);
        } else {
          setTimeout(() => {
            this.loaderService.stop();
            this.phoneVerified = true;
            this.orgDisplay();
          }, 2000);
        }
      },
      error: (error) => {
        this.invalidOTP = true;
        this.loaderService.stop();
        const msg = Utilities.getErrorMessage(error);
        this.toast.error(msg);
      },
    });
    this.loaderService.start();
  }

  orgDisplay() {
    if (!this.loginViaEmail && this.phoneVerified) {
      // Extracting required details
      try {
        if (this.successResponse.multi == true) {
          this.organizationService.setOrgToLocalStorage(true, this.successResponse.organizations);

          this.orgDetails = this.successResponse.organizations.map((org: any) => {
            const address = org.orgAddress[0]; // Assuming there's always at least one address
            return {
              response: org,
              orgName: address.orgName || '',
              orgCode: address.orgCode || '',
              address1: address.address1 || '',
              address2: address.address2 || '',
              postCode: address.postCode || '',
              city: address.city || '',
              fullAddress: Utilities.orgAddressFilter(address),
            };
          }).sort((a: any, b: any) => a.orgName.localeCompare(b.orgName));
        } else {
          this.organizationService.setOrgToLocalStorage(false);
          const address = this.successResponse?.organizations[0].orgAddress[0];
          this.orgDetails = [
            {
              response: this.successResponse.organizations[0],
              orgName: address.orgName || '',
              orgCode: address.orgCode || '',
              address1: address.address1 || '',
              address2: address.address2 || '',
              postCode: address.postCode || '',
              city: address.city || '',
            },
          ];
        }
      } catch (err) {
        this.newOrgRegister();
      }
    }
  }

  loginUsingOrg(org: any) {
    this.loaderService.start();

    if (isPlatformBrowser(this.platformId)) {
      this.authService.setAuthData(org.response);
    }
    this.authService.callForUpdateLastLogin(org.email, org.orgCode);
    this.router.navigate(['dashboard', 'live-vehicles-availability']);
  }

  newOrgRegister() {
    this.emailNumber = true;
    this.phoneVerified = false;
    if (this.decryptedData) {
      this.personalDetails = {
        firstName: this.decryptedData.firstName || '',
        lastName: this.decryptedData.lastName || '',
        company: this.decryptedData.company || null,
        jobtitle: null,
        orgCategory: (this.isZapiet || this.isShopify) ? (this.decryptedData.orgCategory || 'BUS') : (this.decryptedData.orgCategory || null),
      };
    } else if (this.isZapiet || this.isShopify) {
      this.personalDetails.orgCategory = 'BUS';
    }
  }

  signUp() {
    this.router.navigate(['/quote-request/register']);
  }

  placeHolder() {
    if (this.personalDetails.orgCategory != null) {
      delete this.errors['orgCategoryError'];
    } else this.orgCategoryPlaceholder = 'Select your user type';

    if (this.personalDetails.jobtitle != null) {
      delete this.errors['jobtitle'];
    } else this.JobTitlePlaceholder = 'Select your job title';
  }

  companyNameChanged() {
    if (this.personalDetails.company != '') delete this.errors['companyNameError'];
  }

  postalFormating() {
    this.manualAddress.postCode = ValidatorUtil.formatPostalCode(this.manualAddress.postCode);
  }

  async getPositionDetails(address: any) {
    try {
      const encodedAddress = encodeURIComponent(address);
      const response = await this.addressService.getPositions(encodedAddress).toPromise();
      return response;
    } catch (error) {
      console.error('Error fetching Positions:', error);
      throw error;
    }
  }

  getPostalCode(addressComponents: any) {
    for (const component of addressComponents) {
      if (component.types.includes('postal_code')) {
        return component.long_name;
      }
    }
    return null; // Return null if no postal_code is found
  }

  getPostalTown(addressComponents: any) {
    for (const component of addressComponents) {
      if (component.types.includes('postal_town')) {
        return component.long_name;
      }
    }
    return null; // Return null if no postal_code is found
  }

  getCountry(addressComponents: any) {
    for (const component of addressComponents) {
      if (component.types.includes('country')) {
        return component.short_name;
      }
    }
    return null; // Return null if no postal_code is found
  }

  GetSelectedAddress(predictionDescription: string) {
    const matchedPrediction = this.wholeAddresses.find((p: any) => p.description === predictionDescription);

    return matchedPrediction;
  }

  clearError(field: string) {
    if (this.errors[field]) delete this.errors[field];
    if (field === 'firstName') delete this.errors['firstNameSingleWord'];
    if (field === 'lastName') delete this.errors['lastNameSingleWord'];
  }

  async decryptAES(encryptedBase64: string): Promise<string | null> {
    try {
      const encryptedData = this.base64ToArrayBuffer(decodeURIComponent(encryptedBase64));
      const keyBytes = new TextEncoder().encode(this.key);
      const ivBytes = new TextEncoder().encode(this.iv);

      const cryptoKey = await crypto.subtle.importKey('raw', keyBytes, { name: 'AES-CBC' }, false, ['decrypt']);

      const decryptedBuffer = await crypto.subtle.decrypt(
        {
          name: 'AES-CBC',
          iv: ivBytes,
        },
        cryptoKey,
        encryptedData
      );

      return new TextDecoder().decode(decryptedBuffer);
    } catch (error) {
      console.error('Decryption failed:', error);
      return null;
    }
  }

  base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }

  resetDecryptedData(): void {
    this.decryptedData = {
      email: '',
      phone: '',
      firstName: '',
      lastName: '',
      orgCategory: '',
      company: '',
      jobTitle: '',
    };
  }

  onKeyPress(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    if (target && target.type === 'tel') {
      const isDigit = /^[0-9]$/.test(event.key);
      if (!isDigit) {
        event.preventDefault();
      }
    }
  }

  onManualAddressToggle() {
    if (this.showAddressManually) {
      this.manualAddress = {
        address1: this.dropdownAddress.address1,
        address2: this.dropdownAddress.address2,
        city: this.dropdownAddress.city,
        postCode: this.dropdownAddress.postCode,
        establishment: this.dropdownAddress.establishment,
        countryCode: this.dropdownAddress.countryCode,
        additionalDetails: this.dropdownAddress.additionalDetails
      };
    }
    else {
      this.clearManualAddressValidators();
    }
  }

  onSubmit(otpLogin: boolean = false) {
    this.emailError = null;
    this.submitted = false;

    if (otpLogin) {
      this.submitted = true;
      this.checkOTP();
      return;
    }

    if (this.loginViaEmail) {
      // Clear previous errors
      this.emailError = null;
      // Check if email is empty
      if (!this.emailOrMobile) {
        this.emailError = 'Email is required.';
        return;
      }
      // Check if email format is invalid
      if (!Utilities.EMAIL_REGEX.test(this.emailOrMobile)) {
        this.emailError = 'Invalid email format.';
        return;
      }

      this.successResponse = null;

      if (!this.successResponse) {
        this.submitEmailOrMobile(this.emailOrMobile);
      } else {
        this.checkOTP();
      }
    } else {
      // Handle phone validation
      this.phoneForm.markAllAsTouched(); // Ensure validation errors are shown
      if (this.phoneForm.invalid) {
        this.emailError = 'Invalid phone number.';
        this.loaderService.stop();
        return;
      }

      const phoneNumber: any = this.phoneForm.get('phone')?.value;
      this.oldEmail = this.emailOrMobile;
      this.emailOrMobile = phoneNumber.e164Number;

      if (!phoneNumber) {
        this.emailError = 'Please enter a phone number.';
        this.submitted = true;

        this.loaderService.stop();
        return;
      }
      this.submitEmailOrMobile(this.emailOrMobile);
    }
  }

  checkingErrors(): boolean {
    this.formatNameFields();

    if (!this.personalDetails.firstName.trim()) this.errors['firstName'] = true;
    else if (!this.isSingleWord(this.personalDetails.firstName)) this.errors['firstNameSingleWord'] = true;

    if (!this.personalDetails.lastName.trim()) this.errors['lastName'] = true;
    else if (!this.isSingleWord(this.personalDetails.lastName)) this.errors['lastNameSingleWord'] = true;

    if (!this.dropdownAddress.address.trim() && !this.showAddressManually) this.errors['address'] = true;

    if (this.personalDetails.orgCategory == '') this.errors['orgCategoryError'] = true;
    if (
      this.personalDetails.orgCategory != 'NAT' &&
      this.personalDetails.orgCategory != '' &&
      this.personalDetails.jobtitle == ''
    )
      this.errors['jobtitle'] = true;
    if (
      this.personalDetails.orgCategory != 'NAT' &&
      this.personalDetails.orgCategory != '' &&
      this.personalDetails.company == ''
    )
      this.errors['companyNameError'] = true;

    if (this.showAddressManually) {
      if (!this.manualAddress.address1.trim()) {
        this.errors['address1Required'] = true;
        this.errors['address1Length'] = false;
      } else if (this.manualAddress.address1.trim().length > 50) {
        this.errors['address1Required'] = false;
        this.errors['address1Length'] = true;
      } else {
        this.errors['address1Required'] = false;
        this.errors['address1Length'] = false;
      }
      if (this.manualAddress.address2.trim().length > 50) {
        this.errors['address2Length'] = true;
      } else {
        this.errors['address2Length'] = false;
      }
      this.manualAddress.additionalDetails.trim() ? this.errors['additionalDetailsRequired'] = false : this.errors['additionalDetailsRequired'] = true;
      if (!this.manualAddress.city.trim()) this.errors['city'] = true;
      if (!this.manualAddress.postCode.trim() && !this.isPostalCodeValid) this.errors['postCode'] = true;
    }

    if (!this.dropdownAddress.additionalDetails.trim() && !this.showAddressManually) {
      this.showAddressManually = true;
      this.onManualAddressToggle()
      this.errors['additionalDetailsRequired'] = true;
    }

    if (Object.values(this.errors).some((val) => val === true)) {
      this.loaderService.stop();
      return false;
    }

    return true;
  }

  private formatNameFields(): void {
    this.personalDetails.firstName = this.formatName(this.personalDetails.firstName);
    this.personalDetails.lastName = this.formatName(this.personalDetails.lastName);
  }

  private formatName(value: string): string {
    const name = value.trim();
    return name ? name.charAt(0).toUpperCase() + name.slice(1) : '';
  }

  private isSingleWord(value: string): boolean {
    return !/\s/.test(value.trim());
  }

  get isPostalCodeValid(): boolean {
    return ValidatorUtil.postalValidation(this.manualAddress.postCode)
  }

  clearManualAddressValidators() {
    this.errors['address1Length'] = false;
    this.errors['address1Required'] = false;
    this.errors['city'] = false;
    this.errors['postCode'] = false;
  }

  async onSubmitInfo() {
    this.loaderService.start();
    this.errors = {};

    if (this.showAddressManually) {
      if (!this.checkingErrors() || !this.isPostalCodeValid) {
        this.loaderService.stop()
        return;
      }
    }
    else {
      if (!this.checkingErrors()) {
        this.loaderService.stop()
        return;
      }
    }

    let registerData;
    let addressSource = this.showAddressManually ? this.manualAddress : this.dropdownAddress;

    registerData = {
      firstName: this.personalDetails.firstName,
      lastName: this.personalDetails.lastName,
      orgCategory: this.personalDetails.orgCategory,
      orgName:
        this.personalDetails.orgCategory != 'NAT'
          ? this.personalDetails.company
          : `${this.personalDetails.firstName} ${this.personalDetails.lastName}`,
      phone: this.emailOrMobile,
      address1: this.showAddressManually ? addressSource.additionalDetails + ", " + addressSource.address1 : addressSource.address1,
      address2: addressSource.address2,
      email: this.oldEmail,
      countrycode: addressSource.countryCode,
      city: addressSource.city,
      postCode: addressSource.postCode,
      unloco: '',
      jobTitle: this.personalDetails.jobtitle,
      shipments: '',
      page: 'odda-registration',
      eventCode: 'NCA',
      orgType: {
        IsConsignee: 'true',
        IsConsignor: 'true',
        IsForwarder: 'false',
        IsShippingProvider: 'false',
        IsGlobalAccount: 'false',
      },
      Orgcode: '',
      OrgDebtorGroup: null,
      Platform: this.platformUtilities.getOS(),
      isShopify: this.isShopify,
    };
    this.registerUser(registerData);
  }

  registerUser(data: any) {
    this.organizationService
      .submitSameDayRegistration(data)
      .pipe(finalize(() => (this.loaderService.stop())))
      .subscribe({
        next: (response) => {
          this.loaderService.stop();
          if (this.isZapiet) {
            const email = data.email || this.oldEmail || this.emailOrMobile;
            const phone = data.phone || this.emailOrMobile;
            const orgCode =
              response?.data?.orgCode ||
              response?.orgCode ||
              response?.data?.organizationCode ||
              response?.organizationCode ||
              response?.data?.orgAddress?.[0]?.orgCode ||
              data.Orgcode ||
              '';
            this.handleZapietPostRegistration(email, phone, orgCode);
          } else if (this.isShopify) {
            this.generatedApiKey = response?.apiKey || '';
            this.openApiKeyModal();
          } else {
            import('bootstrap').then((bootstrap) => {
              const modalElement = document.getElementById('successModal');
              if (modalElement) {
                const successModal = new bootstrap.Modal(modalElement, {
                  backdrop: 'static', // prevent closing on backdrop click
                  keyboard: false, // prevent closing on ESC key
                });
                successModal.show();
              } else {
                console.error('Modal element not found!');
              }
            });
          }
        },
        error: (error) => {
          const msg = Utilities.getErrorMessage(error);
          this.toast.error(msg);
        },
      });
  }

  async handleZapietPostRegistration(email: string, phone: string, orgCode: string) {
    this.zapietUserData = { email, phone, orgCode, isZapiet: this.isZapiet };
    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.setItem('zapiet_registered_user', JSON.stringify(this.zapietUserData));
        sessionStorage.setItem('zapiet_setup_user', JSON.stringify(this.zapietUserData));
      } catch (e) { }
    }
    await this.openSavePaymentModal();
    await this.initializeCardSetup();
  }

  async openSavePaymentModal() {
    if (!isPlatformBrowser(this.platformId)) return;
    const bootstrap = await import('bootstrap');
    const modalEl = document.getElementById('savePaymentModal');
    if (modalEl) {
      this.savePaymentModalInstance = new bootstrap.Modal(modalEl, {
        backdrop: 'static',
        keyboard: false,
      });
      this.savePaymentModalInstance.show();
    }
  }

  async initializeCardSetup() {
    this.isInitializingCardSetup = true;
    this.cardError = null;

    try {
      // 1. Fetch Stripe publishableKey from public /api/Stripe/config
      const configRes = await firstValueFrom(this.stripeService.getStripeSession());
      const publishableKey = configRes?.publishableKey || configRes?.publishable_key || configRes?.key;
      if (!publishableKey) {
        this.cardError = 'Stripe publishableKey could not be loaded.';
        return;
      }
      this.stripe = await loadStripe(publishableKey);

      // 2. Request Card Setup Intent from backend
      const setupRes = await firstValueFrom(
        this.stripeService.createCardSetupIntent({
          email: this.zapietUserData.email,
          phone: this.zapietUserData.phone,
          orgCode: this.zapietUserData.orgCode,
          isZapiet: this.isZapiet ? true : false,
        })
      );

      if (setupRes?.status === false) {
        this.cardError = setupRes?.message || 'Unable to initialize card setup.';
        return;
      }

      this.setupIntentId = setupRes?.data?.setupIntentId || setupRes?.setupIntentId;
      this.clientSecret = setupRes?.data?.clientSecret || setupRes?.clientSecret;

      if (!this.clientSecret) {
        this.cardError = 'Client secret was not returned by the server.';
        return;
      }

      // 3. Mount Stripe Payment Element
      await this.mountPaymentElement();
    } catch (err: any) {
      console.error('Error initializing card setup:', err);
      this.cardError = Utilities.getErrorMessage(err) || 'Unable to initialize card setup. Please try again.';
    } finally {
      this.isInitializingCardSetup = false;
    }
  }

  private async mountPaymentElement(): Promise<void> {
    if (!this.stripe || !this.clientSecret) return;

    if (this.paymentElement) {
      try {
        this.paymentElement.unmount();
      } catch (e) { }
      this.paymentElement = null;
    }

    this.elements = this.stripe.elements({
      clientSecret: this.clientSecret,
      appearance: this.stripeTheme.getExpressAppearance(),
    });

    const paymentElementOptions: any = {
      layout: {
        type: 'accordion',
        defaultCollapsed: false,
      },
      paymentMethodOrder: ['card'],
      wallets: {
        link: 'never',
      },
    };

    this.paymentElement = this.elements.create('payment', paymentElementOptions);
    setTimeout(() => {
      const el = document.getElementById('payment-element');
      if (el && this.paymentElement) {
        el.innerHTML = '';
        this.paymentElement.mount('#payment-element');
      }
    }, 150);
  }

  async saveCardDetails(): Promise<void> {
    if (!this.stripe) {
      this.cardError = 'Stripe has not been initialized.';
      return;
    }

    if (!this.elements) {
      this.cardError = 'Please wait for the card form to load.';
      return;
    }

    if (!this.setupIntentId) {
      this.cardError = 'Setup intent is missing.';
      return;
    }

    this.isSavingCard = true;
    this.cardError = null;

    try {
      if (isPlatformBrowser(this.platformId) && typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('zapiet_setup_user', JSON.stringify({ ...this.zapietUserData, isZapiet: this.isZapiet }));
      }

      const returnUrl = isPlatformBrowser(this.platformId)
        ? window.location.href
        : '';

      const result: any = await this.stripe.confirmSetup({
        elements: this.elements,
        confirmParams: {
          return_url: returnUrl,
        },
        redirect: 'if_required',
      });

      if (result?.error) {
        this.cardError = result.error.message || 'Unable to save card.';
        this.isSavingCard = false;
        return;
      }

      // If in-page 3DS challenge was completed or no redirect was needed
      if (result?.setupIntent?.status === 'succeeded' || result?.setupIntent) {
        const confirmedSetupIntentId = result.setupIntent.id || this.setupIntentId;
        await this.confirmCardOnBackend(confirmedSetupIntentId);
      }
    } catch (err: any) {
      console.error('Error saving card:', err);
      this.cardError = Utilities.getErrorMessage(err) || 'Unable to save card. Please try again.';
      this.isSavingCard = false;
    }
  }

  private async handleSetupResult(params: any): Promise<void> {
    const clientSecret = params['setup_intent_client_secret'];
    const setupIntentIdFromParam = params['setup_intent'];
    const redirectStatus = params['redirect_status'];

    if (!clientSecret && !setupIntentIdFromParam) return;

    if (isPlatformBrowser(this.platformId) && typeof sessionStorage !== 'undefined') {
      const stored = sessionStorage.getItem('zapiet_setup_user');
      if (stored) {
        try {
          this.zapietUserData = JSON.parse(stored);
          if (this.zapietUserData.isZapiet !== undefined) {
            this.isZapiet = !!this.zapietUserData.isZapiet;
          }
        } catch (e) {
          console.error('Error parsing stored user data', e);
        }
      }
    }

    try {
      this.loaderService.start();

      if (redirectStatus === 'failed' || redirectStatus === 'requires_payment_method') {
        const errorMsg = 'Card authentication failed or was cancelled. Please try another card.';
        this.cardError = errorMsg;
        this.toast.error(errorMsg);
        await this.openSavePaymentModal();
        await this.initializeCardSetup();
        return;
      }

      if (redirectStatus === 'succeeded' && setupIntentIdFromParam) {
        await this.confirmCardOnBackend(setupIntentIdFromParam);
        if (isPlatformBrowser(this.platformId) && typeof sessionStorage !== 'undefined') {
          sessionStorage.removeItem('zapiet_setup_user');
        }
        return;
      }

      if (!this.stripe) {
        const configRes = await firstValueFrom(this.stripeService.getStripeSession());
        const publishableKey = configRes?.publishableKey || configRes?.publishable_key || configRes?.key;
        if (publishableKey) {
          this.stripe = await loadStripe(publishableKey);
        }
      }

      if (clientSecret && this.stripe) {
        const result = await this.stripe.retrieveSetupIntent(clientSecret);
        if (result?.setupIntent) {
          if (result.setupIntent.status === 'succeeded') {
            await this.confirmCardOnBackend(result.setupIntent.id);
            if (isPlatformBrowser(this.platformId) && typeof sessionStorage !== 'undefined') {
              sessionStorage.removeItem('zapiet_setup_user');
            }
          } else {
            const failureMsg = result.setupIntent.last_setup_error?.message || 'Card authentication failed. Please try another card.';
            this.cardError = failureMsg;
            this.toast.error(failureMsg);
            await this.openSavePaymentModal();
            await this.initializeCardSetup();
          }
        } else if (result?.error) {
          this.cardError = result.error.message || 'Card setup was not completed.';
          this.toast.error(this.cardError);
          await this.openSavePaymentModal();
          await this.initializeCardSetup();
        }
      }
    } catch (err: any) {
      console.error('Error handling setup result redirect:', err);
      const errMsg = Utilities.getErrorMessage(err) || 'Failed to complete card setup after redirect.';
      this.cardError = errMsg;
      this.toast.error(errMsg);
      await this.openSavePaymentModal();
      await this.initializeCardSetup();
    } finally {
      this.loaderService.stop();
    }
  }

  private async confirmCardOnBackend(setupIntentId: string): Promise<void> {
    try {
      const request = {
        email: this.zapietUserData.email,
        phone: this.zapietUserData.phone,
        orgCode: this.zapietUserData.orgCode,
        setupIntentId: setupIntentId,
        isZapiet: this.isZapiet ? true : false,
      };

      const response = await firstValueFrom(this.stripeService.saveCard(request));

      if (response?.status === false) {
        this.cardError = response.message || 'Unable to verify saved card.';
        this.isSavingCard = false;
        return;
      }

      this.generatedApiKey =
        response?.data?.apiKey ||
        response?.data?.secretApiKey ||
        response?.data?.secretKey ||
        response?.data?.key ||
        response?.apiKey ||
        response?.secretApiKey ||
        '';

      if (this.savePaymentModalInstance) {
        this.savePaymentModalInstance.hide();
      }

      await this.openApiKeyModal();
    } catch (err: any) {
      console.error('Error confirming card on backend:', err);
      this.cardError = Utilities.getErrorMessage(err) || 'Unable to verify saved card.';
    } finally {
      this.isSavingCard = false;
    }
  }

  async openApiKeyModal() {
    if (!isPlatformBrowser(this.platformId)) return;
    const bootstrap = await import('bootstrap');
    const modalEl = document.getElementById('apiKeyModal');
    if (modalEl) {
      this.apiKeyModalInstance = new bootstrap.Modal(modalEl, {
        backdrop: 'static',
        keyboard: false,
      });
      this.apiKeyModalInstance.show();
    }
  }

  closeSavePaymentModal() {
    if (this.savePaymentModalInstance) {
      this.savePaymentModalInstance.hide();
    }
  }

  closeApiKeyModal() {
    if (this.apiKeyModalInstance) {
      this.apiKeyModalInstance.hide();
    }
  }

  copyApiKey() {
    if (!this.generatedApiKey) return;
    if (isPlatformBrowser(this.platformId) && typeof navigator !== 'undefined' && navigator?.clipboard) {
      navigator.clipboard
        .writeText(this.generatedApiKey)
        .then(() => {
          this.isCopied = true;
          this.toast.success('API key copied to clipboard');
          setTimeout(() => {
            this.isCopied = false;
          }, 3000);
        })
        .catch(() => {
          this.toast.error('Failed to copy API key');
        });
    }
  }

  navigateToLogin() {
    this.closeApiKeyModal();
    this.router.navigate(['/login']);
  }
}
